import type { ISearchHit } from "../../AiSearch/types";
import type { AgentTone } from "../searchDialog.types";
import type { ISearchDialogRowStatus } from "./searchDialogRow.types";

/**
 * Readers for the loose `payload` bag on a search hit, whose fields vary by
 * entity and index version. Every reader returns null on an absent or
 * wrongly-typed value and never throws — a missing amount is the normal case.
 */

/** Keys whose numeric value is money. Anything else stays a plain number. */
const AMOUNT_KEYS = ["amount", "total", "balance", "price", "subtotal"] as const;

const STATUS_KEYS = ["status", "state"] as const;

function read(payload: Record<string, unknown> | undefined, keys: readonly string[]): unknown {
    if (!payload) {
        return undefined;
    }
    for (const key of keys) {
        if (payload[key] !== undefined && payload[key] !== null) {
            return payload[key];
        }
        // The service mixes camelCase and snake_case across entity types.
        const snake = key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
        if (payload[snake] !== undefined && payload[snake] !== null) {
            return payload[snake];
        }
    }
    return undefined;
}

function toFiniteNumber(value: unknown): number | null {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string" && value.trim().length > 0) {
        // Some indexes pre-format the string. Strip grouping and symbol rather
        // than render whatever currency the indexer happened to use.
        const cleaned = value.replace(/[^0-9.-]/g, "");
        const parsed = Number(cleaned);
        return cleaned.length > 0 && Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

/** The row's money value, or null when the record has none. */
export function readAmount(hit: ISearchHit): number | null {
    return toFiniteNumber(read(hit.payload, AMOUNT_KEYS));
}

/**
 * Polarity for a status string. Client policy: nothing in the payload carries
 * it. Unknown statuses stay neutral, so a new one reads as plain rather than
 * wrongly good or wrongly bad.
 */
export function statusTone(text: string): AgentTone {
    const normalized = text.trim().toLowerCase();
    if (/overdue|past due|unpaid|declined|rejected|void/.test(normalized)) {
        return "negative";
    }
    if (/paid|approved|complete|posted|closed|signed/.test(normalized)) {
        return "positive";
    }
    if (/review|pending|due soon|awaiting|draft|sent/.test(normalized)) {
        return "warning";
    }
    return "neutral";
}

/** The row's status chip, or null when the record has none. */
export function readStatus(hit: ISearchHit): ISearchDialogRowStatus | null {
    const value = read(hit.payload, STATUS_KEYS);
    if (typeof value !== "string" || value.trim().length === 0) {
        return null;
    }
    const text = value.trim();
    return { text, tone: statusTone(text) };
}
